import { RefObject, useEffect, useState } from 'react'

import { scopeDemoCss } from 'modules/demandElasticity/helpers/scopeDemoCss'

const DEMO_URL = '/demos/demand-elasticity.html?embed=1'
const STYLE_MARKER = 'data-demand-elasticity-demo'
const MAIN_SCRIPT_MARKER = 'refreshChartTab'

type DemandElasticityWindow = Window & {
  __demandElasticityBoot?: () => void
}

let demoRoot: HTMLDivElement | null = null
let scriptExecuted = false
let htmlCache: string | null = null

const injectStyles = (doc: Document) => {
  document.querySelectorAll(`style[${STYLE_MARKER}]`).forEach((node) => node.remove())

  doc.querySelectorAll('style').forEach((style) => {
    const node = style.cloneNode(false) as HTMLStyleElement
    node.setAttribute(STYLE_MARKER, '')
    node.textContent = scopeDemoCss(style.textContent ?? '')
    document.head.appendChild(node)
  })
}

const buildDemoRoot = (html: string) => {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  injectStyles(doc)

  const root = document.createElement('div')
  root.setAttribute('data-demand-elasticity-root', '')
  root.classList.add('embed')

  const main = doc.querySelector('.main')
  if (main) {
    root.appendChild(main.cloneNode(true))
  }

  doc.querySelectorAll('.modal-overlay, .toast').forEach((node) => {
    root.appendChild(node.cloneNode(true))
  })

  if (!scriptExecuted) {
    doc.querySelectorAll('script').forEach((script) => {
      const content = script.textContent?.trim()
      if (!content || !content.includes(MAIN_SCRIPT_MARKER)) {
        return
      }
      const node = document.createElement('script')
      node.textContent = content
      root.appendChild(node)
    })
    scriptExecuted = true
  }

  return root
}

const attachDemo = (container: HTMLElement) => {
  if (!demoRoot) {
    return
  }

  container.replaceChildren(demoRoot)
  ;(window as DemandElasticityWindow).__demandElasticityBoot?.()
}

export const useDemandElasticityDemo = (containerRef: RefObject<HTMLDivElement | null>) => {
  const [isLoading, setIsLoading] = useState(!demoRoot)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    const container = containerRef.current
    if (!container) {
      return
    }

    let cancelled = false

    const load = async () => {
      if (demoRoot) {
        attachDemo(container)
        if (!cancelled) {
          setIsLoading(false)
          setHasError(false)
        }
        return
      }

      setIsLoading(true)
      setHasError(false)

      try {
        if (!htmlCache) {
          const response = await fetch(DEMO_URL)
          if (!response.ok) {
            throw new Error(`Demo load failed: ${response.status}`)
          }
          htmlCache = await response.text()
        }

        if (cancelled) {
          return
        }

        demoRoot = buildDemoRoot(htmlCache)
        attachDemo(container)

        if (!cancelled) {
          setIsLoading(false)
        }
      } catch {
        if (!cancelled) {
          setHasError(true)
          setIsLoading(false)
        }
      }
    }

    void load()

    return () => {
      cancelled = true
      if (demoRoot?.parentElement === container) {
        demoRoot.remove()
      }
    }
  }, [containerRef])

  return { isLoading, hasError }
}
